import { GeneralDataFacade } from '@/_store/general/general.facade';
import { OffersFacade } from '@/_store/offers/offers.facade';
import { Selected } from '@/_store/offers/offers.types';
import { createQueryParams, setSelectedValueBaseOnParams } from '@/_store/offers/offers.utils';
import { deepCompare } from '@/_utils/objects/deep-compare';
import { FavoriteService } from '@/common/favorite/favorite.service';
import { TopSearchMainConfig, TopSearchMainConfigWithoutSearch } from '@/new-top-search/top-search.config';
import { isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, combineLatest, filter, of, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
	selector: 'app-new-top-search',
	templateUrl: './top-search.component.html',
	styleUrls: ['./top-search.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopSearchComponent implements OnInit {
	@Input() isOfferList = false;
	@Input() showSearchOnly = false;
	@Input() clearFiltersOnSearch = false;
	isMobile$ = this.generalDataFacade.isMobile$;
	isDesktop$ = this.generalDataFacade.isMobile$.pipe(map((isMobile) => !isMobile));

	readonly TopSearchMainConfig = TopSearchMainConfig;
	readonly TopSearchMainConfigWithoutSearch = TopSearchMainConfigWithoutSearch;
	readonly isFavoriteRoute$ = toObservable(this.favoriteService.isFavoriteRoute);
	readonly storedFavorites$ = toObservable(this.favoriteService.storedFavorites);
	readonly searchValue = signal('');

	private readonly platformId = inject(PLATFORM_ID);
	private readonly urlParams$ = this.offersFacade.urlParams$;
	private readonly activateRoute = inject(ActivatedRoute);

	constructor(
		private readonly offersFacade: OffersFacade,
		private readonly generalDataFacade: GeneralDataFacade,
		private readonly router: Router,
		private readonly favoriteService: FavoriteService,
	) {}

	ngOnInit(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		combineLatest([
			this.offersFacade.filterOptions$,
			this.activateRoute.queryParams,
			this.offersFacade.offerFiltersAreEmpty$,
			this.offersFacade.selected$,
			this.isFavoriteRoute$,
			this.storedFavorites$,
		])
			.pipe(
				take(1),
				switchMap(([filterOptions, params, isEmpty, selected, isFavoriteRoute]) => {
					if (isEmpty) {
						this.offersFacade.fetchOfferFilter();
						return EMPTY;
					}
					const path = new URL(window.location.href).pathname;
					const match = path.match(/\/wakacje-autokarem\/[^?]*/);
					const result = match ? match[0] : null;
					const slashCount = result ? (result.match(/\//g) || []).length : 0;
					if (slashCount > 0) {
						return of();
					}
					const selectedFromParams: Selected = setSelectedValueBaseOnParams(filterOptions, params);
					return of({ filterOptions, params, selected, selectedFromParams, isFavoriteRoute });
				}),
				filter(({ selectedFromParams, selected, isFavoriteRoute }) =>
					isFavoriteRoute ? true : !deepCompare(selectedFromParams, selected),
				),
			)
			.subscribe(({ selectedFromParams, isFavoriteRoute }) => {
				if (isFavoriteRoute) {
					selectedFromParams.products = localStorage.getItem('favoriteOffers')
						? JSON.parse(localStorage.getItem('favoriteOffers'))
						: [];
				}
				this.offersFacade.updateSelectedOptions(selectedFromParams);
				this.offersFacade.reloadOfferList();
			});
	}

	goToOfferList(): void {
		this.commitSearchFilterFromInput();

		this.urlParams$.pipe(take(1)).subscribe((params) => {
			const queryParams = createQueryParams(params);
			this.router.navigate(['/wakacje-autokarem'], {
				queryParams,
			});
		});
	}

	clearAllFilters(): void {
		this.offersFacade.clearSelectedOptions();
		this.offersFacade.reloadOfferList();
	}

	onSearchInputBlur(): void {
		this.commitSearchFilterFromInput();
	}

	searchByName(): void {
		const search = this.searchValue().trim();
		if (!search.length) {
			return;
		}

		if (this.clearFiltersOnSearch) {
			this.offersFacade.clearSelectedOptions();
			this.offersFacade.updateSearchFilter(search);
			this.offersFacade.clearSelectedProducts();
			this.offersFacade.reloadOfferList();
		} else {
			this.commitSearchFilterFromInput();
		}

		this.urlParams$.pipe(take(1)).subscribe((params) => {
			const queryParams = createQueryParams(params);
			this.router.navigate(['/wakacje-autokarem'], {
				queryParams,
			});
		});
	}

	private commitSearchFilterFromInput(): void {
		const search = this.searchValue().trim();

		this.offersFacade.selected$.pipe(take(1)).subscribe((selected) => {
			if (selected.search === search) {
				return;
			}

			this.offersFacade.updateSearchFilter(search);
			this.offersFacade.clearSelectedProducts();
			this.offersFacade.reloadOfferList();

			this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
				if (this.isOfferList && isMobile) {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				}
			});
		});
	}
}
